import { lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { IconChevronLeft, IconChevronRight, IconX } from "@tabler/icons-react";
import { DesktopDialogOverlay } from "../app/DesktopDialogOverlay";
import { Button, buttonVariants } from "../ui/Button";
import { createMemoryStore } from "../../lib/store";
import Loader from "../Loader";

const LazyPdfViewer = lazy(() => import("../PdfViewer"));

// The page turned to, for the document on screen: reopening another document
// starts it on its first page.
const usePaging = createMemoryStore<{
  url: string | null;
  page: number;
  count: number | null;
}>({ url: null, page: 1, count: null });

/**
 * A congress document read over its space page (Figma
 * "DesktopCongressPdfViewer"), one page at a time, so the reader keeps their
 * place: the arrows beside the page or ← → turn it, Escape, ✕ or a click
 * outside closes it. Same in-app renderer as the mobile PDF page, in a dialog
 * instead of a route.
 */
export function DesktopCongressPdfViewer({
  label,
  title,
  organisationName,
  accentColor,
  pdfUrl,
  onClose,
}: {
  label: string;
  title: string;
  organisationName: string;
  accentColor: string;
  pdfUrl: string;
  onClose: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [stored, setPaging] = usePaging();
  const paging =
    stored.url === pdfUrl ? stored : { url: pdfUrl, page: 1, count: null };
  const canGoBack = paging.page > 1;
  const canGoForward = paging.count !== null && paging.page < paging.count;
  // Bounded on both sides: no page turns before the document says how many
  // it has.
  const turn = (step: -1 | 1) => {
    if (paging.count === null) return;
    const next = paging.page + step;
    if (next < 1 || next > paging.count) return;
    setPaging({ ...paging, page: next });
  };
  return (
    <DesktopDialogOverlay
      label={title}
      onClose={onClose}
      cardClassName="flex h-full max-h-205 w-full max-w-240 flex-col overflow-hidden rounded-2xl bg-ds-white font-dm-sans"
    >
      {/* The arrow keys turn the page from anywhere in the dialog, where the
          focus stays while it is open. */}
      <div
        className="flex min-h-0 flex-1 flex-col"
        onKeyDown={(event) => {
          // The page column scrolls too: once zoomed, an unhandled arrow
          // would pan the page as it turns it.
          if (event.key === "ArrowLeft") {
            event.preventDefault();
            turn(-1);
          }
          if (event.key === "ArrowRight") {
            event.preventDefault();
            turn(1);
          }
        }}
      >
        <div className="flex items-center gap-4 border-b border-ds-slate-100 px-6 py-4">
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <p
              className="m-0 text-footnote font-medium"
              style={{ color: accentColor }}
            >
              {t("{{label}} · PDF · Shared by {{organisationName}}", {
                label,
                organisationName,
              })}
            </p>
            <p className="m-0 truncate text-footnote font-semibold text-ds-black">
              {title}
            </p>
          </div>
          {paging.count === null ? null : (
            <span className="shrink-0 text-footnote text-ds-slate-500">
              {t("Page {{page}} of {{count}}", {
                page: paging.page,
                count: paging.count,
              })}
            </span>
          )}
          <a
            href={pdfUrl}
            download
            target="_blank"
            rel="noreferrer"
            className={buttonVariants({ variant: "outline", size: "sm" })}
          >
            {t("Download")}
          </a>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("Close")}
            className="flex size-8 cursor-pointer items-center justify-center rounded-md border-none bg-transparent p-0 text-ds-slate-700 hover:bg-ds-slate-100"
          >
            <IconX className="size-5" stroke={1.5} />
          </button>
        </div>
        <div className="relative min-h-0 flex-1 bg-ds-slate-50">
          {/* The page's own column is PdfViewer's scroll viewport (its
              parent): panning and zoom anchoring scroll it. Focusable, so a
              click on the page keeps the arrow keys in the dialog. It spans
              the space between the arrows: the page, portrait or landscape,
              fits in it whole. */}
          <div className="h-full px-20 py-10">
            <div
              data-autofocus
              tabIndex={-1}
              className="size-full overflow-auto outline-none"
            >
              <Suspense fallback={<Loader expanded />}>
                <LazyPdfViewer
                  url={pdfUrl}
                  loadingFallback={<Loader expanded />}
                  controls={{
                    kind: "inline",
                    zoomOutLabel: t("Zoom out"),
                    zoomInLabel: t("Zoom in"),
                    fitLabel: null,
                    start: null,
                    end: null,
                  }}
                  display={{
                    kind: "paged",
                    page: paging.page,
                    fit: "page",
                    onPageCount: (count) => {
                      setPaging({
                        url: pdfUrl,
                        page: Math.min(paging.page, count),
                        count,
                      });
                    },
                  }}
                />
              </Suspense>
            </div>
          </div>
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label={t("Previous page")}
            // Not `disabled`: a disabled button drops the focus to the body,
            // where the arrow keys no longer reach the dialog.
            aria-disabled={!canGoBack}
            onClick={() => {
              turn(-1);
            }}
            className="absolute top-1/2 left-6 -translate-y-1/2 rounded-full bg-ds-white aria-disabled:cursor-not-allowed aria-disabled:opacity-40"
          >
            <IconChevronLeft stroke={1.5} />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label={t("Next page")}
            aria-disabled={!canGoForward}
            onClick={() => {
              turn(1);
            }}
            className="absolute top-1/2 right-6 -translate-y-1/2 rounded-full bg-ds-white aria-disabled:cursor-not-allowed aria-disabled:opacity-40"
          >
            <IconChevronRight stroke={1.5} />
          </Button>
        </div>
        <div className="flex items-center gap-4 border-t border-ds-slate-100 px-6 py-3 text-footnote">
          <span style={{ color: accentColor }}>
            {t("Shared by {{organisation}}", {
              organisation: organisationName,
            })}
          </span>
          <span className="ml-auto text-ds-slate-500">
            {t("← → to change page · Esc to close")}
          </span>
        </div>
      </div>
    </DesktopDialogOverlay>
  );
}
